"use client";
import { useState, useEffect, useCallback, startTransition, memo } from "react";
import { useRouter } from "next/navigation";
import { runIdle } from "@/src/utils/idle-utils";
import PropTypes from "prop-types";
import dynamic from "next/dynamic";
import classNames from "classnames";

import CloseButton from "assets/svg/Icon_Small_Close.svg";

import { getPropertyTree } from "@/src/services/common";
import { urlMaker } from "@/src/services/url-maker";

import { getGa4SearchEventParams } from "src/helpers/google-tag-manager/event-helpers/search";
import { formGTMEventGA4 } from "src/helpers/google-tag-manager/common";

import Dropdown from "components/common/dropdown";
import CheckBoxDropdown from "components/common/checkbox-dropdown";
import RangeSelectDropdown from "components/common/range-select-dropdown";
import Input from "components/common/input";
import Button from "components/common/button";
import Unit from "@/src/utils/constants/units";

import styles from "./styles.module.css";

const SuggestionBox = dynamic(() => import("components/common/suggestion-box"), { ssr: false });
const MemoSuggestionBox = memo(SuggestionBox);

const RentPanel = ({ rentCategories, pageType }) => {
  const router = useRouter();
  const isNotFoundPage = pageType !== "not-found";
  const categories = [];
  rentCategories?.child?.forEach((item) => {
    const rentData = {
      key: item?.id,
      value: item?.name,
      ...item,
    };
    categories.push(rentData);
  });
  const [selected, setSelected] = useState(categories[0]);
  const [roomCountList, setRoomCountList] = useState([]);
  const [roomCountValues, setRoomCountValues] = useState([]);
  const [buildAgeList, setBuildAgeList] = useState([]);
  const [buildAgeValues, setBuildAgeValues] = useState([]);
  const [starCountList, setStarCountList] = useState([]);
  const [starCountValues, setStarCountValues] = useState([]);
  const [priceList, setPriceList] = useState([]);
  const [priceValues, setPriceValues] = useState([]);
  const [squareList, setSquareList] = useState([]);
  const [squareValues, setSquareValues] = useState({});
  const [freeText, setFreeText] = useState("");
  const [poies, setPoies] = useState([]);
  const [locationKey, setLocationkey] = useState("");
  const [inputPlaceholder, setInputPlaceholder] = useState("İl, ilçe, mahalle, site, okul, metro…");

  const locationWrapperClass = classNames({
    [styles.locationinput]: true,
    [styles.locationinputWidth]: isNotFoundPage,
  });

  const poiButtonClass = classNames({
    [styles.poiButtonText]: true,
    [styles.poiButtonTextNotFound]: !isNotFoundPage,
  });

  const closeIconClass = classNames({
    [styles.icon]: true,
    [styles.iconNotFound]: !isNotFoundPage,
  });

  const searchButtonClass = classNames({
    [styles.searchbutton]: true,
    [styles.searchbuttontop]: pageType === "not-found",
  });

  const handleChange = useCallback((selected) => {
    setSelected(selected);
  }, []);

  const fillFilterState = (data, key, setstate) => {
    const values = data?.result?.find((item) => item.key === key);
    const newList = values?.config?.values?.map((item) => ({
      key: item?.id,
      value: item?.name,
    }));

    setstate(newList);
  };

  const getFilters = async (categoryId, tradeTypeId) => {
    const data = await getPropertyTree({
      categoryId,
      tradeTypeId,
    });

    fillFilterState(data, "room_count", setRoomCountList);
    fillFilterState(data, "build_age", setBuildAgeList);
    fillFilterState(data, "star_count", setStarCountList);
    fillFilterState(data, "square", setSquareList);

    const price = data?.result?.find((item) => item.key === "price");
    const newPriceList = price?.config?.values?.map((item) => {
      const splitPrice = item.name.split(" TL");
      return { key: item?.id, value: splitPrice[0] };
    });

    setPriceList(newPriceList);
  };

  useEffect(() => {
    getFilters(selected.key, selected.tradeTypeId);
    if (selected.value !== "Konut" || selected.value !== "Günlük Kiralık Konut") {
      setRoomCountValues([]);
    }
    if (selected.value !== "Arsa") {
      setSquareValues({});
    }
    if (selected.value !== "İşyeri") {
      setBuildAgeValues([]);
    }
    if (selected.value !== "Turistik Tesis") {
      setStarCountValues([]);
    }
  }, [selected]);

  const handleChangeText = useCallback((e) => {
    setFreeText(e.target.value);
  }, []);

  const handlePOIClick = useCallback((item, key) => {
    if (key === "projects") {
      window.location = item?.url;
    }
    const location = item;
    setInputPlaceholder("");
    setFreeText("");
    setPoies([location]);
    setLocationkey(key);
  }, []);

  const handlePoiCloseButtonClick = useCallback(() => {
    setPoies([]);
    setInputPlaceholder("İl, ilçe, mahalle, site, okul, metro…");
  }, []);

  const handleSearchButton = useCallback(
    async (isRotatedToMapPage) => {
      const isNumberOfFreeText = !isNaN(freeText);
      const criteria = {
        category: {
          key: "category",
          category: {
            id: selected?.key,
            slug: selected?.categorySlug,
          },
          tradeType: {
            id: selected?.tradeTypeId,
            slug: selected?.tradeTypeSlug,
          },
        },
        location:
          poies.length > 0
            ? {
                city: poies[0]?.city?.id
                  ? {
                      id: poies[0]?.city.id,
                      name: poies[0]?.city.name,
                      slug: poies[0]?.city.slug,
                    }
                  : {},
                districts: poies[0]?.district?.id
                  ? [
                      {
                        id: poies[0]?.district.id,
                        name: poies[0]?.district.name,
                        slug: poies[0]?.district.slug,
                      },
                    ]
                  : [],
                towns:
                  poies[0].type === "town"
                    ? [
                        {
                          id: poies[0]?.id,
                          name: poies[0]?.name,
                          slug: poies[0]?.slug,
                        },
                      ]
                    : [],
                sites:
                  locationKey === "site"
                    ? [
                        {
                          id: poies[0]?.id,
                          name: poies[0]?.name,
                          displayName: poies[0]?.displayName,
                          url: poies[0]?.url,
                          cityId: poies[0]?.cityId,
                        },
                      ]
                    : [],
                poiSet:
                  locationKey === "poi"
                    ? [
                        {
                          id: poies[0]?.id,
                          name: poies[0]?.name,
                          url: poies[0]?.url,
                          slug: poies[0]?.slug,
                          type: "poies",
                        },
                      ]
                    : [],
                singleDistrict: true,
              }
            : null,
        params: [
          {
            key: "price",
            value: {
              min: priceValues?.min || null,
              max: priceValues?.max || null,
              ...(priceValues.min || priceValues.max ? { currency: "try" } : {}),
            },
          },
          {
            key: "star_count",
            value: starCountValues || null,
          },
          {
            key: "build_age",
            value: buildAgeValues || null,
          },
          {
            key: "room_count",
            value: roomCountValues || null,
          },
          {
            key: "square",
            value: squareValues || null,
          },
        ],
        freetext: freeText
          ? {
              key: "free_text",
              value: freeText,
            }
          : null,
      };

      const ga4SearchEventParams = getGa4SearchEventParams({
        btnCategory: isRotatedToMapPage ? "haritada-ara" : "search",
        criteriaState: criteria,
        activeTab: "kiralik",
        poies,
      });

      runIdle(() => {
        formGTMEventGA4({ eventName: "search", eventParameters: ga4SearchEventParams });
      }, 1000);

      const res = await urlMaker(criteria, "default");

       if (freeText && isNumberOfFreeText) {
         const criteriaPath = res?.path;
         const splitedUrl = criteriaPath.split("?filtreler=q=")[1];
         startTransition(() => {
           router.push(`/ilan/${splitedUrl}`);
         });
         return;
       }

      if (!isRotatedToMapPage) {
        startTransition(() => {
          router.push(res?.path);
        });
      } else {
        const url = res?.path?.includes("?") ? `${res?.path}&view_type=map` : `${res?.path}?view_type=map`;
        startTransition(() => {
          router.push(url);
        });
      }
    },
    [
      freeText,
      selected,
      poies,
      locationKey,
      priceValues,
      starCountValues,
      buildAgeValues,
      roomCountValues,
      squareValues,
      router,
    ]
  );

  const handleSearchClick = useCallback(() => handleSearchButton(false), [handleSearchButton]);
  const handleMapSearchClick = useCallback(() => handleSearchButton(true), [handleSearchButton]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        handleSearchClick();
        return;
      }
      if (freeText?.length === 0) {
        if (event.key === "Backspace") {
          setInputPlaceholder("İl, ilçe, mahalle, site, okul, metro…");
          setPoies([]);
        }
      }
    },
    [freeText, handleSearchClick]
  );

  const handleChangeRoomCountCheckbox = useCallback((item) => {
    const newItem = item.map(({ key, value, ...rest }) => ({ id: key, name: value, ...rest }));
    startTransition(() => {
      setRoomCountValues(newItem);
    });
  }, []);

  const handleChangeBuildAgeCheckbox = useCallback((item) => {
    const newItem = item.map(({ key, value, ...rest }) => ({ id: key, name: value, ...rest }));
    startTransition(() => {
      setBuildAgeValues(newItem);
    });
  }, []);

  const handleChangeStarCountCheckbox = useCallback((item) => {
    const newItem = item.map(({ key, value, ...rest }) => ({ id: key, name: value, ...rest }));
    startTransition(() => {
      setStarCountValues(newItem);
    });
  }, []);

  const handleChangeSquareRange = useCallback((min = null, max = null) => {
    startTransition(() => {
      setSquareValues({ min: min?.value, max: max?.value });
    });
  }, []);

  const handlePriceRange = useCallback((min = null, max = null) => {
    const numberMin = isNaN(min) ? Number(min?.value?.split(".").join("")) : min;
    const numberMax = isNaN(max) ? Number(max?.value?.split(".").join("")) : max;
    startTransition(() => {
      setPriceValues({ min: numberMin, max: numberMax });
    });
  }, []);

  return (
    <div className={styles.tabpanel}>
      <div className={styles.field}>
        {isNotFoundPage && <span className={styles.title}>Gayrimenkul Tipi</span>}
        <div className={styles.typeinput}>
          <Dropdown options={categories} onChange={handleChange} />
        </div>
      </div>
      <div className={`${styles.wfull} ${styles.field}`}>
        {isNotFoundPage && <span className={styles.title}>Konum Bilgisi</span>}
        <div className={locationWrapperClass}>
          {poies?.map((poi, index) => (
            <div key={index} className={styles.buttonWrapper}>
              <Button tooltip={poi?.name} flow="down" size="Auto" buttonClass={styles.buttonClass}>
                <span className={poiButtonClass}>{poi?.name}</span>
                <CloseButton onClick={() => handlePoiCloseButtonClick()} className={closeIconClass} />
              </Button>
            </div>
          ))}
          <div className={styles.inputWrapper}>
            <Input
              placeholder={inputPlaceholder}
              onChange={handleChangeText}
              value={freeText}
              inputClass={styles.inputClass}
              onKeyDown={handleKeyDown}
            />
          </div>
        </div>
        <MemoSuggestionBox
          freeText={freeText}
          categoryId={selected?.key}
          tradeTypeId={selected?.tradeTypeId}
          handlePOIClick={handlePOIClick}
        />
      </div>
      {(selected.value === "Konut" || selected.value === "Günlük Kiralık Konut") && isNotFoundPage && (
        <div className={styles.field}>
          <span className={styles.title}>Oda Sayısı</span>
          <div className={styles.roominput}>
            <CheckBoxDropdown
              placeHolder="Oda Giriniz"
              listClass={styles.checkboxwidth}
              options={roomCountList}
              onChange={handleChangeRoomCountCheckbox}
            />
          </div>
        </div>
      )}
      {selected.value === "Arsa" && isNotFoundPage && (
        <div className={styles.field}>
          <span className={styles.title}>Metrekare</span>
          <div className={styles.roominput}>
            <RangeSelectDropdown
              listClass={styles.rangeWidth}
              minList={squareList}
              maxList={squareList}
              type={Unit.M2.name}
              onChange={handleChangeSquareRange}
            />
          </div>
        </div>
      )}
      {selected.value === "İşyeri" && isNotFoundPage && (
        <div className={styles.field}>
          <span className={styles.title}>Binanın Yaşı</span>
          <div className={styles.roominput}>
            <CheckBoxDropdown
              placeHolder="Seçiniz"
              listClass={styles.checkboxwidth}
              options={buildAgeList}
              onChange={handleChangeBuildAgeCheckbox}
            />
          </div>
        </div>
      )}
      {selected.value === "Turistik Tesis" && isNotFoundPage && (
        <div className={styles.field}>
          <span className={styles.title}>Yıldız Sayısı</span>
          <div className={styles.roominput}>
            <CheckBoxDropdown
              placeHolder="Seçiniz"
              listClass={styles.checkboxwidth}
              options={starCountList}
              onChange={handleChangeStarCountCheckbox}
            />
          </div>
        </div>
      )}
      {isNotFoundPage && (
        <div className={styles.field}>
          <span className={styles.title}>Fiyat Bilgisi</span>
          <div className={styles.priceinput}>
            <RangeSelectDropdown
              placeHolder="Fiyat Giriniz"
              listClass={styles.rangeWidth}
              minList={priceList}
              maxList={priceList}
              onChange={handlePriceRange}
              type="TL"
            />
          </div>
        </div>
      )}
      <div className={`${styles.field} h-full flex items-end`}>
        <div className={searchButtonClass}>
          <Button onClick={handleSearchClick} size="Auto">
            Ara
          </Button>
        </div>
      </div>
      {isNotFoundPage && (
        <div className="h-full flex items-end">
          <div className={searchButtonClass}>
            <Button onClick={handleMapSearchClick} size="Auto" outline buttonClass={styles.mapButtonClass}>
              Haritada Ara
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

RentPanel.propTypes = {
  rentCategories: PropTypes.object,
  pageType: PropTypes.string,
};

export default RentPanel;
